<template>
  <AppLayout>
    <div class="max-w-2xl mx-auto">
      <transition name="fade-slide" appear>
        <div class="bg-white rounded-xl shadow p-6">
          <transition name="fade-slide" appear>
            <h2 class="text-2xl font-bold text-tiye-dark mb-6">Profile Settings</h2>
          </transition>

          <!-- Profile Picture Section -->
          <div class="mb-8">
            <div class="flex items-center gap-6">
              <div class="relative">
                <transition name="scale-fade" mode="out-in">
                  <img
                    :key="currentUser?.photoURL"
                    :src="currentUser?.avatar || `https://ui-avatars.com/api/?name=${getInitials(currentUser?.email || '')}&background=random`"
                    :alt="currentUser?.email"
                    class="w-24 h-24 rounded-full border-2 border-tiye-green object-cover hover-lift"
                  />
                </transition>
                <transition name="fade-scale">
                  <label
                    for="photo-upload"
                    class="absolute bottom-0 right-0 bg-tiye-green text-white p-2 rounded-full cursor-pointer hover:bg-tiye-dark transition-all hover-lift active-press"
                  >
                    <span class="material-icons text-sm">edit</span>
                  </label>
                </transition>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handlePhotoUpload"
                />
              </div>
              <transition name="fade-slide" appear>
                <div>
                  <h3 class="text-lg font-semibold text-tiye-dark">{{ displayName || currentUser?.email }}</h3>
                  <p class="text-tiye-dark/60">Update your profile information</p>
                </div>
              </transition>
            </div>
          </div>

          <!-- Profile Information Form -->
          <transition name="fade-slide" appear>
            <form @submit.prevent="handleProfileUpdate" class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <transition name="fade-slide" appear>
                  <div>
                    <label class="block text-sm font-medium text-tiye-dark mb-1">Name</label>
                    <input
                      v-model="name"
                      type="text"
                      class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-tiye-green focus:ring-2 focus:ring-tiye-green/20 outline-none transition-all hover-lift"
                      placeholder="Enter name"
                    />
                  </div>
                </transition>
                <transition name="fade-slide" appear>
                  <div>
                    <label class="block text-sm font-medium text-tiye-dark mb-1">Surname</label>
                    <input
                      v-model="surname"
                      type="text"
                      class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-tiye-green focus:ring-2 focus:ring-tiye-green/20 outline-none transition-all hover-lift"
                      placeholder="Enter surname"
                    />
                  </div>
                </transition>
              </div>

              <transition name="fade-slide" appear>
                <div>
                  <label class="block text-sm font-medium text-tiye-dark mb-1">Email</label>
                  <input
                    v-model="email"
                    type="email"
                    class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-tiye-green focus:ring-2 focus:ring-tiye-green/20 outline-none transition-all hover-lift"
                    placeholder="Enter email"
                  />
                </div>
              </transition>

              <transition name="fade-slide" appear>
                <div class="pt-4">
                  <button
                    type="submit"
                    class="w-full px-6 py-3 bg-tiye-green text-white font-semibold rounded-xl hover:bg-tiye-dark hover:text-tiye-green transition-all hover-lift active-press"
                    :disabled="isUpdating"
                  >
                    <span v-if="isUpdating" class="flex items-center justify-center gap-2">
                      <span class="material-icons animate-spin">refresh</span>
                      Updating...
                    </span>
                    <span v-else>Save Changes</span>
                  </button>
                </div>
              </transition>
            </form>
          </transition>

          <!-- Email notifications -->
          <div class="mt-8 pt-8 border-t" style="border-color: var(--move-border)">
            <h3 class="text-xl font-bold move-text mb-1">Email notifications</h3>
            <p class="text-sm move-text-muted mb-5">
              Choose which Move updates we send to {{ email || 'your email' }}.
            </p>

            <div v-if="loadingPreferences" class="flex items-center gap-2 move-text-faint text-sm py-4">
              <span class="material-icons animate-spin text-base">refresh</span>
              Loading preferences...
            </div>

            <div v-else class="space-y-3">
              <label
                v-for="option in emailNotificationOptions"
                :key="option.key"
                class="move-pref-toggle"
              >
                <div class="min-w-0">
                  <p class="font-semibold move-text text-sm">{{ option.label }}</p>
                  <p class="text-xs move-text-faint mt-0.5">{{ option.description }}</p>
                </div>
                <input
                  v-model="notificationPreferences[option.key]"
                  type="checkbox"
                  class="move-pref-toggle__input"
                  @change="saveNotificationPreferences"
                />
                <span class="move-pref-toggle__track" aria-hidden="true">
                  <span class="move-pref-toggle__thumb" />
                </span>
              </label>
            </div>
          </div>

          <!-- Tiye app notifications -->
          <div
            v-if="!loadingPreferences && tiyeApp.available"
            class="mt-8 pt-8 border-t"
            style="border-color: var(--move-border)"
          >
            <div class="flex items-start gap-3 mb-5">
              <span
                class="inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
                style="background: var(--move-accent-soft); color: var(--move-accent)"
              >
                <span class="material-icons">phone_iphone</span>
              </span>
              <div>
                <h3 class="text-xl font-bold move-text mb-1">Tiye app notifications</h3>
                <p class="text-sm move-text-muted">
                  Get Move updates in the Tiye mobile app when you're signed in with
                  <strong class="move-text">{{ tiyeApp.email || email }}</strong>.
                </p>
              </div>
            </div>

            <div
              v-if="!tiyeApp.linked"
              class="rounded-xl px-4 py-3 mb-5 text-sm move-text-muted"
              style="background: var(--move-accent-soft); border: 1px solid var(--move-border)"
            >
              <span class="material-icons text-base align-middle mr-1" style="color: var(--move-accent)">info</span>
              Open the Tiye app and sign in with this email to receive push alerts.
              You can turn these on now — they'll apply once your app is connected.
            </div>

            <div class="space-y-3">
              <label
                v-for="option in appNotificationOptions"
                :key="option.key"
                class="move-pref-toggle"
              >
                <div class="min-w-0">
                  <p class="font-semibold move-text text-sm">{{ option.label }}</p>
                  <p class="text-xs move-text-faint mt-0.5">{{ option.description }}</p>
                </div>
                <input
                  v-model="notificationPreferences[option.key]"
                  type="checkbox"
                  class="move-pref-toggle__input"
                  @change="saveNotificationPreferences"
                />
                <span class="move-pref-toggle__track" aria-hidden="true">
                  <span class="move-pref-toggle__thumb" />
                </span>
              </label>
            </div>
          </div>

          <p v-if="preferencesSaved" class="text-sm mt-4" style="color: var(--move-accent)">
            Notification preferences saved.
          </p>

          <!-- Change Password Section -->
          <div class="mt-8 pt-8 border-t border-gray-100">
            <h3 class="text-xl font-bold text-tiye-dark mb-4">Change Password</h3>
            <form @submit.prevent="handlePasswordChange" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-tiye-dark mb-1">Current Password</label>
                <input
                  v-model="currentPassword"
                  type="password"
                  class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-tiye-green focus:ring-2 focus:ring-tiye-green/20 outline-none transition-all hover-lift"
                  placeholder="Enter current password"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-tiye-dark mb-1">New Password</label>
                <input
                  v-model="newPassword"
                  type="password"
                  class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-tiye-green focus:ring-2 focus:ring-tiye-green/20 outline-none transition-all hover-lift"
                  placeholder="Enter new password"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-tiye-dark mb-1">Confirm New Password</label>
                <input
                  v-model="confirmPassword"
                  type="password"
                  class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-tiye-green focus:ring-2 focus:ring-tiye-green/20 outline-none transition-all hover-lift"
                  placeholder="Confirm new password"
                  required
                />
              </div>
              <div class="pt-4">
                <button
                  type="submit"
                  class="w-full px-6 py-3 bg-tiye-green text-white font-semibold rounded-xl hover:bg-tiye-dark hover:text-tiye-green transition-all hover-lift active-press"
                  :disabled="isChangingPassword"
                >
                  <span v-if="isChangingPassword" class="flex items-center justify-center gap-2">
                    <span class="material-icons animate-spin">refresh</span>
                    Changing Password...
                  </span>
                  <span v-else>Change Password</span>
                </button>
              </div>
            </form>
          </div>

          <!-- Loading State -->
          <transition name="fade-scale">
            <div v-if="isUploading" class="mt-4">
              <div class="flex items-center gap-2 text-tiye-green">
                <span class="material-icons animate-spin">refresh</span>
                <span>Uploading...</span>
              </div>
            </div>
          </transition>

          <!-- Error Message -->
          <transition name="slide-fade">
            <div v-if="error" class="mt-4 p-3 bg-red-50 text-red-600 rounded-lg">
              {{ error }}
            </div>
          </transition>

          <!-- Success Message -->
          <transition name="slide-fade">
            <div v-if="success" class="mt-4 p-3 bg-green-50 text-green-600 rounded-lg">
              {{ success }}
            </div>
          </transition>
        </div>
      </transition>
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { moveApi } from '../services/moveApi'
import AppLayout from '../layouts/AppLayout.vue'

const store = useStore()
const currentUser = computed(() => store.state.auth.user)
const isUploading = ref(false)
const isUpdating = ref(false)
const error = ref('')
const success = ref('')

const name = ref('')
const surname = ref('')
const email = ref('')

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const isChangingPassword = ref(false)
const loadingPreferences = ref(true)
const preferencesSaved = ref(false)
const savingPreferences = ref(false)

const emailNotificationOptions = [
  {
    key: 'board_added',
    label: 'Added to a board',
    description: 'When you are added to a new board'
  },
  {
    key: 'task_assigned',
    label: 'Task assigned to me',
    description: 'When someone assigns you a task'
  },
  {
    key: 'task_updated',
    label: 'Task updates',
    description: 'When a task assigned to you is changed'
  },
  {
    key: 'comment_added',
    label: 'New comments',
    description: 'When someone comments on your tasks'
  }
]

const appNotificationOptions = [
  {
    key: 'app_board_added',
    label: 'Added to a board',
    description: 'Push alert when you join a new board'
  },
  {
    key: 'app_task_assigned',
    label: 'Task assigned to me',
    description: 'Push alert when a task is assigned to you'
  },
  {
    key: 'app_task_updated',
    label: 'Task updates',
    description: 'Push alert when your tasks change'
  },
  {
    key: 'app_comment_added',
    label: 'New comments',
    description: 'Push alert when someone comments on your tasks'
  }
]

const notificationPreferences = ref({
  board_added: true,
  task_assigned: true,
  task_updated: true,
  comment_added: true,
  app_board_added: false,
  app_task_assigned: false,
  app_task_updated: false,
  app_comment_added: false
})

const tiyeApp = ref({
  available: false,
  linked: false,
  email: ''
})

onMounted(async () => {
  name.value = currentUser.value?.name || ''
  surname.value = currentUser.value?.surname || ''
  email.value = currentUser.value?.email || ''

  if (store.state.auth.moveProfile?.email_preferences) {
    notificationPreferences.value = {
      ...notificationPreferences.value,
      ...store.state.auth.moveProfile.email_preferences
    }
  }

  try {
    loadingPreferences.value = true
    const { data } = await moveApi.getNotificationPreferences()
    notificationPreferences.value = { ...notificationPreferences.value, ...(data.data || {}) }
    if (data.tiye_app) {
      tiyeApp.value = { ...tiyeApp.value, ...data.tiye_app }
    }
  } catch (err) {
    console.error('Error loading notification preferences:', err)
  } finally {
    loadingPreferences.value = false
  }
})

const displayName = computed(() => store.getters['auth/displayName'])

const getInitials = (value) => {
  const local = (value || '').split('@')[0]
  const parts = local.split(/[._-]/)
  if (parts.length >= 2) {
    return `${parts[0][0].toUpperCase()}${parts[1][0].toUpperCase()}`
  }
  return local.slice(0, 2).toUpperCase()
}

const saveNotificationPreferences = async () => {
  if (savingPreferences.value) return

  try {
    savingPreferences.value = true
    preferencesSaved.value = false
    error.value = ''

    const { data } = await moveApi.updateNotificationPreferences({ ...notificationPreferences.value })
    if (data.move_profile) {
      store.commit('auth/setMoveProfile', data.move_profile)
    }
    if (data.tiye_app) {
      tiyeApp.value = { ...tiyeApp.value, ...data.tiye_app }
    }
    preferencesSaved.value = true
    setTimeout(() => {
      preferencesSaved.value = false
    }, 2500)
  } catch (err) {
    console.error('Error saving notification preferences:', err)
    error.value = 'Failed to save notification preferences.'
  } finally {
    savingPreferences.value = false
  }
}

const handlePhotoUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  if (!file.type.startsWith('image/')) {
    error.value = 'Please upload an image file'
    return
  }

  if (file.size > 5 * 1024 * 1024) {
    error.value = 'Image size should be less than 5MB'
    return
  }

  try {
    isUploading.value = true
    error.value = ''
    success.value = ''
    await store.dispatch('auth/updateAvatar', file)
    success.value = 'Profile picture updated successfully'
  } catch (err) {
    console.error('Error uploading photo:', err)
    error.value = err.response?.data?.message || 'Failed to update profile picture. Please try again.'
  } finally {
    isUploading.value = false
  }
}

const handleProfileUpdate = async () => {
  try {
    isUpdating.value = true
    error.value = ''
    success.value = ''

    const result = await store.dispatch('auth/updateProfile', {
      name: name.value,
      surname: surname.value
    })

    if (!result.success) {
      error.value = result.error || 'Failed to update profile.'
      return
    }

    success.value = 'Profile updated successfully'
  } catch (err) {
    console.error('Error updating profile:', err)
    error.value = 'Failed to update profile. Please try again.'
  } finally {
    isUpdating.value = false
  }
}

const handlePasswordChange = async () => {
  try {
    isChangingPassword.value = true
    error.value = ''
    success.value = ''

    if (newPassword.value !== confirmPassword.value) {
      error.value = 'New passwords do not match'
      return
    }

    if (newPassword.value.length < 8) {
      error.value = 'New password must be at least 8 characters long'
      return
    }

    const response = await moveApi.changePassword({
      password: currentPassword.value,
      new_password: newPassword.value
    })

    if (!response.data.success) {
      error.value = 'Current password is incorrect'
      return
    }

    currentPassword.value = ''
    newPassword.value = ''
    confirmPassword.value = ''
    success.value = 'Password changed successfully'
  } catch (err) {
    console.error('Error changing password:', err)
    error.value = err.response?.data?.message || 'Failed to change password. Please try again.'
  } finally {
    isChangingPassword.value = false
  }
}
</script>

<style scoped>
/* Fade with slide animation */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Scale with fade animation */
.scale-fade-enter-active,
.scale-fade-leave-active {
  transition: all 0.3s ease;
}

.scale-fade-enter-from,
.scale-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Slide fade animation */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

/* Hover lift effect */
.hover-lift {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.hover-lift:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Active press effect */
.active-press {
  transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);
}

.active-press:active {
  transform: scale(0.98) translateY(1px);
}
</style>
